import Enumerable from "linq";
import {
  NameSpecifiedEntity
} from "../../../../../base/domain/entity";
import {
  Directory,
} from "../../../../../base/domain/drawing";
import {
  DateTimeStringUtility
} from "../../../../../base/domain/system";
import {
  Company, 
} from "../../../../../documaker/domain/format";
import {
  FormatThumbnailSource,
} from "../../../../../documaker/infrastructure/format/ver2";
import {
  IEditableFormatStructure,
  IEditableFormatLayer,
  IEditableFormatReferenceFile,
  IEditableFormatPageStructure,
  IEditableFormatObject,
  EditableFormatSummary, 
  IEditableFormatOperation,
  PaperUtility,
  StoragePlaceKinds,
  IEditableFormatSetting,
  IEditableFormatAlertSetting,
  WriteMethodKinds,
  IEditableFormatQuestionnaire,
} from "../../../../domain/editableFormat";
import {
  IImageObject, instanceOfIImageObject,
  IImageDrawableObject, instanceOfIImageDrawableObject,
  IScriptObject, instanceOfIScriptObject,
} from "../../../../domain/editableFormat/objects";
import { EditableFormatPreviewSource } from "../../../../domain/editableFormat/sources";
import {
  IFormatObjectBean,
  IReferenceFileObjectBean,
  ISectionObjectBean,
  IXElementContentsObjectBean,
  IImageInfoObjectBean,
  IEditableFormatBean,
  IEditableFormatFolderBean,
  IEditableFormatFolderRootBean,
  IEditableFormatOperationBean,
  IFormatSettingBean,
  IXElementContentsObjectRelationalBean,
  IXElementContentsObjectRelationalItemBean,
} from "../beans";
import {
  IImageObjectBean,
} from "../beans/objects";
import {
  ImageObjectTranslator,
} from "./objects";
import {
  IntegerTranslator,
  ArrayComplexTranslator,
} from "./values";
import { ObjectTranslatorRepository } from "./ObjectTranslatorRepository";
import { LoggerFactory } from "../../../../../../utilities/logger";

export class Translator {
  private readonly _version = "3.00";                   // TODO: メンテできるように外部から入れられるようにしたい.
  private readonly _applicationVersion = "2.00.000.00"; // TODO: メンテできるように外部から入れられるようにしたい.v1で読んだ時を考えるとフォーマットは揃えたい.(見てるらしい.)
  private _objectTranslatorRepository: ObjectTranslatorRepository = new ObjectTranslatorRepository();
  private _integer = new IntegerTranslator();
  private readonly _arrayComplex = new ArrayComplexTranslator();

  toFormatStructure(bean: IFormatObjectBean): IEditableFormatStructure {
    return {
      version: bean.formatInfo.version,
      id: bean.formatInfo.id,
      name: bean.formatInfo.name,
      edition: bean.formatInfo.edition,
      documentKindId: bean.formatInfo.reportTypeId,
      companyCode: bean.formatInfo.companyCode,
      masterCode: bean.formatInfo.masterCode,
      expirationDate: {
        from: bean.formatInfo.startDateTime ? DateTimeStringUtility.toDateTimeValue(bean.formatInfo.startDateTime) : null,
        to: bean.formatInfo.endDateTime ? DateTimeStringUtility.toDateTimeValue(bean.formatInfo.endDateTime) : null,
        shouldContainCurrent: false,
      },
      numberOfPrints: bean.formatInfo.defaultCopies ? bean.formatInfo.defaultCopies : 1,
      indicationMagnification: bean.formatInfo.indicationMagnification ? bean.formatInfo.indicationMagnification : 100,
      categoryType: bean.formatInfo.categoryType ? bean.formatInfo.categoryType : "1",
      tag: bean.formatInfo.tag,
      inOutType: bean.formatInfo.inOutType ? bean.formatInfo.inOutType : "0",
      referenceFiles: this.toFormatReferenceFiles(bean.referenceFiles),
      lastMaxObjectId: this.getBeanLastMaxObjectId(bean.sections),
      maxPageKey: !!bean.formatInfo.maxPageKey ? bean.formatInfo.maxPageKey : bean.sections.length, // 新規時にはmaxPageKeyがないためsections数を入れているが、見直しも検討する
      pages: this.toFormatPageStructures(bean.sections),
      lastUpdated: {
        dateTime: bean.formatInfo.lastUpdateDateTime ? DateTimeStringUtility.toDateTimeValue(bean.formatInfo.lastUpdateDateTime) : null,
        userName: bean.formatInfo.lastUpdatedUserName,
      },
      isDeleted: bean.formatInfo.deleted,
      archiveId: bean.formatInfo.archiveId,
      copiedFormat: {
        id: bean.formatInfo.copiedFormatId,
        archiveId: bean.formatInfo.copiedFormatArchiveId,
      },
      writeMethodType: this.toWriteMethodType(bean.formatInfo.writeMethodType),
      settings: this.toFormatSetting(bean.setting),
      questionnaire: this.toQuestionnaire(bean.sections),
    };
  }

  private toQuestionnaire(beans: ISectionObjectBean[]): IEditableFormatQuestionnaire {
    const found = Enumerable.from(beans)
      .selectMany(b => this._arrayComplex.from(b.contents.printformat.page.objects))
      .firstOrDefault(b => !!this.extractRelational(b, "questionnaire-body"));
    const relational = this.extractRelational(found, "questionnaire-body");
    return {
      resourceType: this.extractRelationalItemKeyValue(relational, "resourceType", "questionnaire"),
      version: this.extractRelationalItemKeyValue(relational, "version", "1"),
      status: this.extractRelationalItemKeyValue(relational, "status", "draft"),
      publisher: this.extractRelationalItemKeyValue(relational, "publisher", ""),
      experimental: this.extractRelationalItemKeyValue(relational, "experimental", "false").toLowerCase() === "true",
      copyright: this.extractRelationalItemKeyValue(relational, "copyright", ""),
      subjectTypes: this.extractRelationalItemKeyValue(relational, "subjectTypes", "Patient").split(","),
    };
  }

  private extractRelationalItemKeyValue(source: IXElementContentsObjectRelationalBean, key: string, defaultValue: string | undefined): string | undefined {
    return this.extractRelationalItem(source, s => s["@key"] === key, s => s["@value"], defaultValue);
  }

  private extractRelationalItem(source: IXElementContentsObjectRelationalBean, predicate: (s: IXElementContentsObjectRelationalItemBean) => boolean, toValue: (s: IXElementContentsObjectRelationalItemBean) => string, defaultValue: string | undefined): string | undefined {
    const found = this._arrayComplex.from(source ? source.relationalitem : []).find(predicate);
    return found
      ? toValue(found)
      : defaultValue;
  }

  private extractRelational(source: IXElementContentsObjectBean, action: string): IXElementContentsObjectRelationalBean {
    return Enumerable.from(this._arrayComplex.from(source ? source.relational : [])).firstOrDefault(r => r["@action"] === action);
  }

  private getBeanLastMaxObjectId(beans: ISectionObjectBean[]): number {
    const bean = Enumerable.from(beans).firstOrDefault();
    return !bean ? 0 : parseInt(bean.contents.printformat["@maxobj-id"], 10);
  }

  private toFormatReferenceFiles(beans: IReferenceFileObjectBean[]): IEditableFormatReferenceFile[] {
    return Enumerable.from(beans)
      .select((b, i) => this.toFormatReferenceFile(b))
      .toArray();
  }

  private toFormatReferenceFile(bean: IReferenceFileObjectBean): IEditableFormatReferenceFile {
    return {
      key: bean.fileKey,
      name: bean.fileName,
      storagePlace: bean.storagePlace,
    };
  }

  private toFormatPageStructures(beans: ISectionObjectBean[]): IEditableFormatPageStructure[] {
    return Enumerable.from(beans)
      .select((b, i) => this.toFormatPageStructure(b))
      .toArray();
  }

  private toFormatPageStructure(bean: ISectionObjectBean): IEditableFormatPageStructure {
    const allObjects = this.toFormatPageObjects(bean.contents.printformat.page.objects, bean.formatId);
    const backgroundImageObject = this.toIImageObject(allObjects.find(v => instanceOfIImageObject(v) && v.isBackground));
    const editableFormatObjects = allObjects.filter(v => !(instanceOfIImageObject(v) && v.isBackground));
    return {
      pageKey: !!bean.pageKey ? bean.pageKey : bean.sectionNumber,  // 新規時にはpageKeyがないためsectionNumberを入れているが、見直しも検討する
      size: {
        width: PaperUtility.pointToPixel(parseFloat(bean.contents.printformat.page["@paperwidth"])),
        height: PaperUtility.pointToPixel(parseFloat(bean.contents.printformat.page["@paperheight"])),
      },
      isHorizontal: bean.landscape,
      image: {
        location: {
          x: PaperUtility.pointToPixel(parseFloat(bean.contents.printformat.page["@imagex"])),
          y: PaperUtility.pointToPixel(parseFloat(bean.contents.printformat.page["@imagey"])),
        },
        size: {
          width: PaperUtility.pointToPixel(parseFloat(bean.contents.printformat.page["@imagewidth"])),
          height: PaperUtility.pointToPixel(parseFloat(bean.contents.printformat.page["@imageheight"])),
        },
      },
      layer: this.toIEditableFormatLayer(bean.backgroundImage, bean.formatId, backgroundImageObject, bean.sectionNumber),
      shouldPrint: bean.printEnable,
      shouldPrintOnFirstRegistered: bean.printOnSaving,
      editingOnly: backgroundImageObject ? backgroundImageObject.editingOnly : false,
      objects: editableFormatObjects,
    };
  }

  private toWriteMethodType(bean: string): WriteMethodKinds {
    const writeMethodType = this._integer.from(bean);
    return writeMethodType ? writeMethodType : WriteMethodKinds.Unknown;
  }

  private toFormatSetting(bean: IFormatSettingBean): IEditableFormatSetting {
    return {
      nameAlert: this.toFormatAlertSetting(bean),
    };
  }

  private toFormatAlertSetting(bean: IFormatSettingBean): IEditableFormatAlertSetting {
    return {
      names: (!bean || !bean.Setting.Alerts) 
        ? [] 
        : Enumerable.from(Array.isArray(bean.Setting.Alerts.Alert) ? bean.Setting.Alerts.Alert : [ bean.Setting.Alerts.Alert ])
          .select(alert => alert["@Name"]).toArray()
    };
  }

  private toIImageObject(object: IEditableFormatObject): IImageObject {
    return instanceOfIImageObject(object) ? object : null;
  }

  private toIEditableFormatLayer(bean: IImageInfoObjectBean, formatId: number, object: IImageObject, sectionNo: number): IEditableFormatLayer {
    const imageKey = bean ? bean.imageKey : null;
    const result: IEditableFormatLayer = {
      formatId: imageKey ? formatId : null,
      sectionNo: imageKey ? sectionNo : null,
      imageGuid: imageKey,
      location: object ? object.location : { x: 0, y: 0, },
      size: object ? object.size : { width: 0, height: 0, },
      fileName: "",
    };
    (result as any)._sourceObject = object; // 書き戻す際に他の情報が欠落しないように保持しておく.(変換時以外意識したくないのでIEditableFormatLayerには定義しない.)
    return result;
  }

  toFormatPageObjects(beans: IXElementContentsObjectBean[] | IXElementContentsObjectBean, formatId: number): IEditableFormatObject[] {
    if (!beans) {
      return [];
    }
    const results = Enumerable.from(Array.isArray(beans) ? beans : [ beans ])
      .select((b, i) => this.toFormatPageObject(b, formatId))
      .toArray();
    this.complementScriptTableSize(results);  // TODO: script依存の実装.上手く抽象化したい所.
    return results;
  }

  toFormatPageObject(bean: IXElementContentsObjectBean, formatId: number): IEditableFormatObject {
    if (bean === null) {
      return null;
    }
    const translator = this._objectTranslatorRepository.getTranslator(bean["@element-name"]);
    const result = translator.fromBean(bean, formatId);
    return result;
  }

  private complementScriptTableSize(objects: IEditableFormatObject[]): void {
    const scriptObjects = Enumerable.from(objects)
      .where(o => instanceOfIScriptObject(o))
      .cast<IScriptObject>()
      .toArray();
    const originScriptObjects = Enumerable.from(scriptObjects)
      .where(o => (o.scriptTableBehavior.index.row === 0) && (o.scriptTableBehavior.index.column === 0))
      .toArray();
    for (const originScriptObject of originScriptObjects) {
      const tableObjects = Enumerable.from(scriptObjects)
        .where(o => o.name === originScriptObject.name)
        .toArray();
      originScriptObject.scriptTableBehavior.size = {
        row: Enumerable.from(tableObjects).max(o => o.scriptTableBehavior.index.row + 1),
        column: Enumerable.from(tableObjects).max(o => o.scriptTableBehavior.index.column + 1),
      };
    }
  }

  toFormatsTree(getToken: () => string, bean: IEditableFormatFolderRootBean): Directory<number, EditableFormatSummary>[] {
    if (!bean) { return []; }
    return this.toFormatsDirectories(getToken, bean.folders);
  }

  private toFormatsDirectories(getToken: () => string, beans: IEditableFormatFolderBean[]): Directory<number, EditableFormatSummary>[] {
    return Enumerable.from(beans)
      .select((b, i) => this.toFormatsDirectory(getToken, b, i))
      .toArray();
  }

  private toFormatsDirectory(getToken: () => string, bean: IEditableFormatFolderBean, index: number): Directory<number, EditableFormatSummary> {
    const directory = new Directory(
      bean.id,
      bean.name,
      this.toFormatsDirectories(getToken, bean.folders),
      this.toFormats(getToken, bean.formats),
      bean.sort
    );
    // Vueのリアクティブに登録すると、プロパティがリアクティブなgetter/setterに置換されてメモリを大量に使用してしまうため、
    // 読み取りしかしないオブジェクトについてはObject.freezeすることでリアクティブにさせないようにしている
    Object.freeze(directory);
    return directory;
  }

  public toFormats(getToken: () => string, beans: IEditableFormatBean[]): EditableFormatSummary[] {
    return Enumerable.from(beans)
      .select(b => this.toFormat(getToken, b))
      .toArray();
  }

  public toFormat(getToken: () => string, bean: IEditableFormatBean): EditableFormatSummary {
    const editableFormatSummary = new EditableFormatSummary(
      bean.id,
      bean.name,
      bean.edition,
      bean.version,
      bean.isTemporary,
      bean.lastUpdateDateTime ? DateTimeStringUtility.toDateTimeValue(bean.lastUpdateDateTime) : null,  // Momentを使用すると変換速度に10倍以上差がついてしまうので自前実装のものを使用する。
      bean.lastUpdatedUserName,
      new EditableFormatPreviewSource(getToken, bean.id, bean.archiveId),
      new FormatThumbnailSource(getToken, bean.id),
      bean.reportTypeId,
      bean.masterCode,
      bean.tag,
      new Company(bean.companyCode, bean.companyName),
      bean.categoryType,
      bean.writeMethodType,
      bean.startDateTime ? DateTimeStringUtility.toDateTimeValue(bean.startDateTime) : null,  // Momentを使用すると変換速度に10倍以上差がついてしまうので自前実装のものを使用する。,
      bean.endDateTime ? DateTimeStringUtility.toDateTimeValue(bean.endDateTime) : null,  // Momentを使用すると変換速度に10倍以上差がついてしまうので自前実装のものを使用する。,
      bean.deleted,
      bean.archiveId,
      bean.sort
    );
    // Vueのリアクティブに登録すると、プロパティがリアクティブなgetter/setterに置換されてメモリを大量に使用してしまうため、
    // 読み取りしかしないオブジェクトについてはObject.freezeすることでリアクティブにさせないようにしている
    Object.freeze(editableFormatSummary);
    return editableFormatSummary;
  }

  public toFormatOperations(beans: IEditableFormatOperationBean[]): IEditableFormatOperation[] {
    return Enumerable.from(beans)
      .select(b => this.toFormatOperation(b))
      .toArray();
  }

  private toFormatOperation(bean: IEditableFormatOperationBean): IEditableFormatOperation {
    const iEditableFormatOperation = {
      dateTime: bean.operationDateTime ? DateTimeStringUtility.toDateTimeValue(bean.operationDateTime) : null,  // Momentを使用すると変換速度に10倍以上差がついてしまうので自前実装のものを使用する。
      user: new NameSpecifiedEntity<string>(bean.userId, bean.userName),
      target: new NameSpecifiedEntity<number>(bean.targetId, bean.targetName),
      targetKind: bean.targetKind,
      operationKind: bean.operationKind,
      oldValue: bean.oldId ? new NameSpecifiedEntity<number>(bean.oldId, bean.oldName) : null,
      newValue: bean.newId ? new NameSpecifiedEntity<number>(bean.newId, bean.newName) : null,
    };
    // Vueのリアクティブに登録すると、プロパティがリアクティブなgetter/setterに置換されてメモリを大量に使用してしまうため、
    // 読み取りしかしないオブジェクトについてはObject.freezeすることでリアクティブにさせないようにしている
    Object.freeze(iEditableFormatOperation);
    return iEditableFormatOperation;
  }

  toFormatObjectBean(format: IEditableFormatStructure, isTemporary: boolean): IFormatObjectBean {
    const result: IFormatObjectBean = {
      formatInfo: {
        id: format.id,
        name: format.name ? format.name : "",
        edition: format.edition,
        version: this._version,
        isTemporary: isTemporary,
        lastUpdateDateTime: format.lastUpdated.dateTime ? DateTimeStringUtility.fromDateTimeValue(format.lastUpdated.dateTime) : null,
        lastUpdatedUserName: format.lastUpdated.userName,
        reportTypeId: format.documentKindId,
        masterCode: format.masterCode,
        tag: format.tag,
        companyCode: format.companyCode,
        companyName: null,                // companyNameは渡さない.(バックエンドがマスタから補完するので.)
        categoryType: format.categoryType,
        writeMethodType: this._integer.to(format.writeMethodType),
        startDateTime: format.expirationDate.from ? DateTimeStringUtility.fromDateTimeValue(format.expirationDate.from) : null,
        endDateTime: format.expirationDate.to ? DateTimeStringUtility.fromDateTimeValue(format.expirationDate.to) : null,
        defaultCopies: format.numberOfPrints,
        indicationMagnification: format.indicationMagnification,
        inOutType: format.inOutType,
        necessaryWarningType: null, // E大での対応は見送り.(ファイル版(v37)には設定するUIがないので.対応する際に型定義から行う事.)
        maxPageKey: format.maxPageKey,
        deleted: format.isDeleted,
        archiveId: format.archiveId,
        copiedFormatArchiveId: format.copiedFormat.archiveId,
        copiedFormatId: format.copiedFormat.id,
      },
      sections: this.toSectionObjectsBean(format.id, format.pages),
      pastedImages: this.toPastedImagesBean(format.pages),
      referenceFiles: this.toReferenceFilesBean(format.id, format.referenceFiles),
      setting: this.toFormatSettingBean(format.settings),
    };
    this.applyQuestionnaireBody(result, format.questionnaire);
    return result;
  }

  private applyQuestionnaireBody(source: IFormatObjectBean, questionnaire: IEditableFormatQuestionnaire): void {
    const firstObject = Enumerable.from(this._arrayComplex.from(source.sections)).selectMany(s => this._arrayComplex.from(s.contents.printformat.page.objects)).firstOrDefault();
    if (firstObject) { // 見つからない場合はquestionnaire属性が記録されない.(Questionnaire拡張利用時はobjectなしでは登録できないよう別途ガードする予定.)
      this.replaceRelational(firstObject, {
        "@action": "questionnaire-body",
        "relationalitem": this._arrayComplex.to([
          this.toRelationalItemBeanKeyValue("resourceType", questionnaire.resourceType),
          this.toRelationalItemBeanKeyValue("version", questionnaire.version),
          this.toRelationalItemBeanKeyValue("status", questionnaire.status),
          this.toRelationalItemBeanKeyValue("publisher", questionnaire.publisher),
          this.toRelationalItemBeanKeyValue("experimental", questionnaire.experimental ? "True" : undefined),
          this.toRelationalItemBeanKeyValue("copyright", questionnaire.copyright),
          this.toRelationalItemBeanKeyValue("subjectTypes", questionnaire.subjectTypes.join(",")),
        ].filter(i => !!i)),
      });
    } else {
      LoggerFactory.getLogger().warn("オブジェクトが存在しないのでQuestionnaire拡張属性は保存できません.");
    }
  }

  private toRelationalItemBeanKeyValue(key: string, value: string): IXElementContentsObjectRelationalItemBean | undefined {
    return value
      ? {
        "@key": key,
        "@value": value,
      }
      : undefined;
  }

  private replaceRelational(source: IXElementContentsObjectBean, relational: IXElementContentsObjectRelationalBean): void {
    source.relational = this._arrayComplex.to(this._arrayComplex.from(source.relational).filter(r => r["@action"] !== relational["@action"]).concat(relational));
  }

  private toSectionObjectsBean(formatId: number, pages: IEditableFormatPageStructure[]): ISectionObjectBean[] {
    const lastMaxObjectId = this.getLastMaxObjectId(pages);
    const now = new Date();
    return Enumerable.from(pages)
      .select((p, i) => this.toSectionObjectBean(formatId, p, i, lastMaxObjectId, now))
      .toArray();
  }

  private getLastMaxObjectId(pages: IEditableFormatPageStructure[]): number {
    return Enumerable.from(pages)
      .selectMany(p => p.objects)
      .where(o => !!o && !!o.id)
      .select(o => o.id)
      .concat([0])  // idがあるオブジェクトが存在しないとmaxが例外投げてしまうので0を追加しておく.
      .max();
  }

  private toSectionObjectBean(formatId: number, page: IEditableFormatPageStructure, index: number, lastMaxObjectId: number, now: Date): ISectionObjectBean {
    return {
      formatId: formatId,
      sectionNumber: index,
      pageKey: page.pageKey,
      pageSize: PaperUtility.sizeToKind(page.size),
      landscape: page.isHorizontal,
      printEnable: page.shouldPrint,
      printOnSaving: page.shouldPrintOnFirstRegistered,
      contents: {
        printformat: {
          "@version": this._version,
          "@app-version": this._applicationVersion,
          "@createdate": DateTimeStringUtility.fromDateTimeValue(now),
          "@maxobj-id": lastMaxObjectId.toString(),
          "page": {
            "@paperwidth": PaperUtility.pixelToPoint(page.size.width).toString(),
            "@paperheight": PaperUtility.pixelToPoint(page.size.height).toString(),
            "@imagex": PaperUtility.pixelToPoint(page.image.location.x).toString(),
            "@imagey": PaperUtility.pixelToPoint(page.image.location.y).toString(),
            "@imagewidth": PaperUtility.pixelToPoint(page.image.size.width).toString(),
            "@imageheight": PaperUtility.pixelToPoint(page.image.size.height).toString(),
            "@orientation": page.isHorizontal ? "0" : "1",
            "objects": this.toIXElementContentsObjectsBean(page.objects, this.toIImageObjectBean(page)),
          },
        },
      },
      backgroundImage: this.toIImageInfoObjectBean(page.layer),
    };
  }

  private toIImageObjectBean(page: IEditableFormatPageStructure): IImageObjectBean {
    return page.layer.imageGuid
      ? this.toLayerImageObjectBean(page)
      : null;
  }

  private toLayerImageObjectBean(page: IEditableFormatPageStructure): IImageObjectBean {
    const translator = new ImageObjectTranslator();
    const sourceObject = (page.layer as any)._sourceObject as IImageObject;
    const object = sourceObject
      ? sourceObject
      : translator.fromBean({
        "@name": "",
        "@x": "0",
        "@y": "0",
        "@width": "0",
        "@height": "0",
        "@edit": "1",
        "@path": "",
        "@bg": "1",
        "@element-name": "image",
      }, null);
    object.location = page.layer.location;
    object.size = page.layer.size;
    object.temporaryImageContent = {
      guid: page.layer.imageGuid,
      isEnable: true,
    };
    object.editingOnly = page.editingOnly;
    return translator.toBean(object);
  }

  toIXElementContentsObjectsBean(objects: IEditableFormatObject[], backgroundImage: IXElementContentsObjectBean): IXElementContentsObjectBean | IXElementContentsObjectBean[] {
    const baseArray = backgroundImage ? [backgroundImage] : [];
    return this._arrayComplex.to(baseArray.concat(Enumerable.from(objects)
      .select(o => this.toIXElementContentsObjectBean(o))
      .toArray()));
  }

  private toIXElementContentsObjectBean(object: IEditableFormatObject): IXElementContentsObjectBean {
    const translator = this._objectTranslatorRepository.getTranslator(object.elementName);
    const result = translator.toBean(object);
    return result;
  }

  private toIImageInfoObjectBean(content: IEditableFormatLayer): IImageInfoObjectBean {
    return content && content.imageGuid
      ? {
        imageKey: content.imageGuid,
        storagePlace: content.formatId ? StoragePlaceKinds.Format : StoragePlaceKinds.Cache,
      }
      : {
        imageKey: null,
        storagePlace: StoragePlaceKinds.Format,
      };
  }

  private toPastedImagesBean(pages: IEditableFormatPageStructure[]): IImageInfoObjectBean[] {
    return Enumerable.from(pages)
      .selectMany(p => this.toSectionPastedImagesBean(p))
      .distinct(o => o.imageKey)
      .toArray();
  }

  private toSectionPastedImagesBean(page: IEditableFormatPageStructure): IImageInfoObjectBean[] {
    return Enumerable.from(page.objects)
      .select(o => this.toIImageDrawableObject(o))
      .where(o => !!o && (o.imageContent.isEnable || o.temporaryImageContent.isEnable))
      .select(o => this.toPastedImageBean(o))
      .toArray();
  }

  private toIImageDrawableObject(object: IEditableFormatObject): IImageDrawableObject {
    return instanceOfIImageDrawableObject(object, "image") || instanceOfIImageDrawableObject(object, "imagearea")
      ? object
      : null;
  }

  private toPastedImageBean(object: IImageDrawableObject): IImageInfoObjectBean {
    return object.temporaryImageContent.isEnable
      ? {
        imageKey: object.temporaryImageContent.guid,
        storagePlace: StoragePlaceKinds.Cache,
      }
      : {
        imageKey: object.imageContent.guid,
        storagePlace: StoragePlaceKinds.Format,
      };
  }

  private toReferenceFilesBean(formatId: number, referenceFiles: IEditableFormatReferenceFile[]): IReferenceFileObjectBean[] {
    return Enumerable.from(referenceFiles)
      .select(r => this.toReferenceFileBean(formatId, r))
      .toArray();
  }

  private toReferenceFileBean(formatId: number, referenceFile: IEditableFormatReferenceFile): IReferenceFileObjectBean {
    return {
      formatId: formatId,
      fileKey: referenceFile.key,
      fileName: referenceFile.name,
      storagePlace: referenceFile.storagePlace,
    };
  }

  private toFormatSettingBean(setting: IEditableFormatSetting): IFormatSettingBean {
    if (setting.nameAlert.names.length < 1) { return null; }
    return {
      Setting: {
        Alerts: {
          Alert: this._arrayComplex.to(Enumerable.from(this._arrayComplex.from(setting.nameAlert.names)).select(s => {
            return {
              "@Name": s,
            };
          }).toArray()),
        },
      },
    };
  }



  toFormatFolderRootBean(folders: Directory<number, EditableFormatSummary>[]): IEditableFormatFolderRootBean {
    return {
      folders: this.toFormatFoldersBean(folders),
    };
  }

  private toFormatFoldersBean(folders: Directory<number, EditableFormatSummary>[]): IEditableFormatFolderBean[] {
    return Enumerable.from(folders)
      .select(f => this.toFormatFolderBean(f))
      .toArray();
  }

  private toFormatFolderBean(folder: Directory<number, EditableFormatSummary>): IEditableFormatFolderBean {
    return {
      id: folder.key,
      name: folder.name,
      folders: this.toFormatFoldersBean(folder.nodes),
      formats: this.toFormatsBean(folder.entities),
      sort: folder.sort
    };
  }

  private toFormatsBean(formats: EditableFormatSummary[]): IEditableFormatBean[] {
    return Enumerable.from(formats)
      .select(f => this.toFormatBean(f))
      .toArray();
  }

  private toFormatBean(format: EditableFormatSummary): IEditableFormatBean {
    return {
      id: format.id,
      name: format.name,
      edition: format.edition,
      version: format.version,
      isTemporary: format.isTemporary,
      lastUpdateDateTime: format.lastUpdateDateTime ? DateTimeStringUtility.fromDateTimeValue(format.lastUpdateDateTime) : null,
      lastUpdatedUserName: format.lastUpdatedUserName,
      reportTypeId: format.reportTypeId,
      masterCode: format.masterCode,
      tag: format.tag,
      companyCode: format.company ? format.company.id : null,
      companyName: format.company ? format.company.name : null,
      categoryType: format.categoryType,
      writeMethodType: format.writeMethodType,
      startDateTime: format.startDateTime ? DateTimeStringUtility.fromDateTimeValue(format.startDateTime) : null,
      endDateTime: format.endDateTime ? DateTimeStringUtility.fromDateTimeValue(format.endDateTime) : null,
      defaultCopies: null,
      indicationMagnification: null,
      inOutType: null,
      necessaryWarningType: null,
      maxPageKey: null,
      deleted: format.deleted,
      archiveId: format.archiveId,
    };
  }
}
